import React, { useEffect, useState, useMemo } from "react";
import { getProjects } from "../api/projectAPI";
import { Helmet } from "react-helmet";
import { motion } from "framer-motion";
import { FaEye, FaExternalLinkAlt } from "react-icons/fa";
import ProjectModal from "../components/ProjectModal";
import {
  GridSkeleton,
  ProjectCardSkeleton,
} from "../components/LoadingSkeleton";
import { isImageUrl } from "../utils/media";

const Projects = () => {
  const [projects, setProjects] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedProject, setSelectedProject] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeTag, setActiveTag] = useState("all");

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const projectsData = await getProjects();
        setProjects(Array.isArray(projectsData) ? projectsData : []);
        setError(null);
      } catch (error) {
        if (process.env.NODE_ENV === "development") {
          console.error("Error fetching projects:", error);
        }
        setError("Failed to load projects.");
      } finally {
        setLoading(false); // Stop loading once data is fetched
      }
    };

    fetchProjects();
  }, []);

  const handleProjectClick = (project) => {
    setSelectedProject(project);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedProject(null);
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0 },
  };

  const allTags = useMemo(
    () => [
      ...new Set(
        projects.flatMap((p) => (Array.isArray(p.tags) ? p.tags : []))
      ),
    ],
    [projects]
  );

  const displayedProjects = useMemo(() => {
    if (activeTag === "all") return projects;
    return projects.filter((p) =>
      (p.tags || []).includes(activeTag)
    );
  }, [projects, activeTag]);

  if (error) {
    return <div className="text-red-500 text-center">{error}</div>;
  }

  return (
    <>
      <Helmet>
        <title>Projects - David Seibold's Full-Stack Engineer Portfolio</title>
        <meta
          name="description"
          content="Browse David Seibold's full-stack development projects, including front-end and back-end builds. Discover how I combine technical skills with my M&A background."
        />
        <meta
          property="og:title"
          content="Projects - David Seibold's Full-Stack Engineer Portfolio"
        />
        <meta
          property="og:description"
          content="Explore a range of full-stack development projects created by David Seibold."
        />
        {/* <meta
          property="og:image"
          content="https://daveyrockets.me/og-projects-image.jpg"
        />{" "} */}
        {/* Replace with your actual image URL */}
        <meta property="og:url" content="https://daveyrockets.me/projects" />
      </Helmet>
      <div className="container mx-auto px-4 md:px-6 lg:px-8 pb-12 md:pb-16 max-w-7xl text-gray-800 dark:text-brand-cream">
        <motion.h1
          className="heading-hero text-center text-gradient mb-8 py-10"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          My Projects
        </motion.h1>

        {!loading && allTags.length > 0 && (
          <div className="flex flex-wrap justify-center gap-2 mb-8">
            <button
              type="button"
              onClick={() => setActiveTag("all")}
              className={`px-4 py-2 rounded-full text-sm font-medium border transition-colors ${
                activeTag === "all"
                  ? "bg-brand-gold text-black border-brand-gold"
                  : "border-gray-300 dark:border-brand-purple/30 text-gray-700 dark:text-brand-cream hover:border-brand-gold/50"
              }`}
            >
              All
            </button>
            {allTags.map((tag) => (
              <button
                key={tag}
                type="button"
                onClick={() => setActiveTag(tag)}
                className={`px-4 py-2 rounded-full text-sm font-medium border transition-colors ${
                  activeTag === tag
                    ? "bg-brand-gold text-black border-brand-gold"
                    : "border-gray-300 dark:border-brand-purple/30 text-gray-700 dark:text-brand-cream hover:border-brand-gold/50"
                }`}
              >
                #{tag}
              </button>
            ))}
          </div>
        )}

        {loading ? (
          <GridSkeleton
            count={4}
            component={ProjectCardSkeleton}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-8"
          />
        ) : displayedProjects.length > 0 ? (
          <motion.div
            key={activeTag}
            variants={containerVariants}
            initial="hidden"
            animate="show"
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-8"
          >
            {displayedProjects.map((project) => (
              <motion.div
                key={project._id}
                variants={itemVariants}
                className="group card-standard overflow-hidden shadow-lg hover:shadow-2xl hover:shadow-brand-gold/20 transform hover:-translate-y-2"
              >
                {/* Project Preview */}
                <div className="relative w-full h-64 md:h-72 overflow-hidden bg-gradient-to-br from-brand-gold/10 to-brand-purple/10">
                  {project.thumbnail &&
                    (isImageUrl(project.thumbnail) ? (
                      <img
                        src={project.thumbnail}
                        alt={`Screenshot of ${project.title}`}
                        loading="lazy"
                        className="absolute top-0 left-0 w-full h-full object-cover"
                      />
                    ) : (
                      <iframe
                        src={project.thumbnail}
                        className="absolute top-0 left-0 w-full h-full border-0"
                        title={project.title}
                        loading="lazy"
                        sandbox="allow-scripts allow-same-origin"
                        style={{
                          transform: "scale(0.5)",
                          transformOrigin: "top left",
                          width: "200%",
                          height: "200%",
                        }}
                      />
                    ))}

                  {/* Overlay with actions */}
                  <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center space-x-4">
                    <button
                      onClick={() => handleProjectClick(project)}
                      className="px-4 py-2 bg-brand-gold text-black rounded-lg font-semibold hover:bg-brand-gold/90 transition-colors flex items-center gap-2"
                    >
                      <FaEye />
                      View Details
                    </button>
                    <a
                      href={project.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-4 py-2 bg-transparent border-2 border-white text-white rounded-lg font-semibold hover:bg-white hover:text-black transition-colors flex items-center gap-2"
                    >
                      <FaExternalLinkAlt />
                      Live Demo
                    </a>
                  </div>
                </div>

                {/* Project Info */}
                <div>
                  <h2 className="text-xl font-bold text-brand-gold mb-3 group-hover:text-brand-gold/80 transition-colors">
                    {project.title}
                  </h2>
                  <p className="text-body text-sm leading-relaxed line-clamp-3">
                    {project.description}
                  </p>

                  {/* Tech Stack / Tags */}
                  {(Array.isArray(project.techStack) &&
                    project.techStack.length > 0) ||
                  (Array.isArray(project.tags) && project.tags.length > 0) ? (
                    <div className="mt-4 flex flex-wrap gap-2">
                      {(project.techStack || []).slice(0, 6).map((tech) => (
                        <span
                          key={tech}
                          className="px-2 py-1 bg-purple-100 dark:bg-brand-purple/20 text-gray-700 dark:text-brand-cream rounded text-xs font-medium border border-purple-200 dark:border-brand-purple/30"
                        >
                          {tech}
                        </span>
                      ))}
                      {(project.tags || []).slice(0, 4).map((tag) => (
                        <span
                          key={tag}
                          className="px-2 py-1 bg-brand-gold/10 text-brand-gold rounded text-xs font-medium border border-brand-gold/30"
                        >
                          #{tag}
                        </span>
                      ))}
                    </div>
                  ) : null}
                </div>
              </motion.div>
            ))}
          </motion.div>
        ) : (
          <motion.div
            className="text-gray-700 dark:text-brand-cream text-center text-lg p-8"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            No projects found. Check back soon for updates!
          </motion.div>
        )}

        {/* Project Modal */}
        <ProjectModal
          project={selectedProject}
          isOpen={isModalOpen}
          onClose={closeModal}
        />
      </div>
    </>
  );
};

export default Projects;
